import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import libraryBg from '../library.jpg'
import { getStoredAuthError, clearStoredAuthError } from '../utils/authError'

export default function LoginPage() {
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // A prior denial is resolved once they're authenticated — clear it so a
      // stale stored error can't pop the modal on a later visit to /login.
      clearStoredAuthError()
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    )
  }

  // A post-login Action denial (e.g. the email-verification gate) comes back as
  // an OAuth `access_denied` error; show the server-authored message verbatim so
  // the wording stays owned by the Auth0 Action across all clients.
  const denial = error as (Error & { error?: string; error_description?: string }) | undefined
  // Fall back to the URL-captured error: the in-memory Auth0 `error` does not
  // reliably survive the origin -> '/' -> '/login' redirect bounce.
  const stored = getStoredAuthError()
  const accessDenied = denial?.error === 'access_denied' || stored?.error === 'access_denied'
  const showNotice = !dismissed && accessDenied
  const noticeText =
    denial?.error_description ||
    stored?.description ||
    denial?.message ||
    'Please verify your email address before signing in.'

  const dismissNotice = () => {
    clearStoredAuthError()
    setDismissed(true)
  }

  return (
    <div className="login-page" style={{ backgroundImage: `url(${libraryBg})` }}>
      <div className="login-overlay" />
      <div className="login-content">
        <div className="login-brand">
          <h1 className="login-title">Eizel</h1>
          <p className="login-subtitle">Your read-later list</p>
        </div>
        <button
          className="login-signin-btn"
          // Force an interactive login instead of silently resuming an
          // existing Auth0 SSO session. A deliberate "Sign in" click must
          // let the user choose / enter which account to use — required
          // because one person can hold several accounts (Gmail dot/+
          // variants), and it guarantees the post-login Actions (verify-
          // email gate + account linking) actually run on this auth.
          onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}
        >
          Log in or Sign up
        </button>
      </div>

      {showNotice && (
        <div
          className="auth-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Verify your email"
        >
          <div className="auth-modal">
            <h2>Verify your email</h2>
            <p>{noticeText}</p>
            <button className="btn-primary" onClick={dismissNotice}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
